import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { _className, ComponentSync, getElementCenterPos, Grid, LL_H_C, LL_V_C, LL_V_L, ModuleFE_MouseInteractivity, ModuleFE_Thunderstorm, mouseInteractivity_PopUp, TS_ComponentTransition, TS_ErrorBoundary, Button } from '@nu-art/thunderstorm-frontend/index';
import { resolveContent, RuntimeVersion } from '@nu-art/ts-common';
import { Component_AccountThumbnail } from '../Component_AccountThumbnail/Component_AccountThumbnail.js';
import './PopUp_AccountMenu.scss';
import { Component_ChangePassword } from '../Component_ChangePassword/Component_ChangePassword.js';
import { ModuleFE_Utils } from '@nu-art/thunderstorm-frontend/modules/ModuleFE_Utils/ModuleFE_Utils';
import { RendererKey_AccountMenu_SubHeader } from '../../consts.js';
import { ModuleFE_Account } from '../../_entity/account/index.js';
export class PopUp_AccountMenu extends ComponentSync {
    // ######################### Static #########################
    static show(e, props, modalProps) {
        ModuleFE_MouseInteractivity.showContent({
            id: 'pop-up__account-menu',
            originPos: getElementCenterPos(e.currentTarget),
            modalPos: modalProps?.modalPos ?? { x: -1, y: 1 },
            content: () => _jsx(PopUp_AccountMenu, { ...props }),
            offset: modalProps?.offset,
        });
    }
    static Action_AppToolsButton = (url) => ({
        label: 'App Tools',
        type: 'action',
        closePopUp: true,
        action: () => {
            ModuleFE_Thunderstorm.openUrl(url, '_blank');
        }
    });
    static Action_EditPassword = {
        label: 'Edit Password',
        type: 'page',
        pageKey: 'edit-password',
        content: (account, closePageTrigger) => _jsx(Component_ChangePassword, { postSubmitAction: closePageTrigger })
    };
    static defaultProps = {
        menuActions: [PopUp_AccountMenu.Action_AppToolsButton('/app-tools'), PopUp_AccountMenu.Action_EditPassword]
    };
    // ######################### Life Cycle #########################
    deriveStateFromProps(nextProps, state) {
        state.account = ModuleFE_Account.cache.unique(nextProps.accountId);
        if (!state.account)
            this.logError('Could not get account', `account id: ${nextProps.accountId}`);
        //Gather menu actions
        state.menuActions = nextProps.menuActions?.map(menuAction => resolveContent(menuAction));
        //Prepare page map from actions
        state.pageMap = state.menuActions?.reduce((map, action) => {
            if (action.type === 'page')
                map[action.pageKey] = action.content;
            return map;
        }, {}) ?? {};
        return state;
    }
    // ######################### Logic #########################
    closePopUp = () => {
        ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
    };
    logOut = async () => {
        this.closePopUp();
        ModuleFE_Account.logout();
    };
    onActionClick = (action, e) => {
        switch (action.type) {
            case 'action': {
                if (action.disabled)
                    return;
                if (action.closePopUp)
                    this.closePopUp();
                action.action(e);
                break;
            }
            case 'page': {
                this.setState({ pageKey: action.pageKey, prevPageKey: action.pageKey });
                break;
            }
            default:
                // @ts-ignore
                this.logError(`Unknown action type ${action.type}`);
        }
    };
    closeCustomPage = () => {
        this.setState({ pageKey: undefined });
    };
    // ######################### Render #########################
    render() {
        return _jsxs(TS_ErrorBoundary, { children: [this.renderNoAccount(), this.renderAccount()] });
    }
    renderNoAccount = () => {
        if (this.state.account)
            return;
        return _jsx("div", { className: 'account-menu__not-found', children: "Account Not Found" });
    };
    renderAccount = () => {
        if (!this.state.account)
            return;
        return _jsxs(_Fragment, { children: [_jsxs(LL_V_L, { className: 'account-menu', children: [this.renderHeader(), this.renderBody(), this.renderFooter()] }), this.renderCustomPage()] });
    };
    renderSeparatorBar = (style) => {
        return _jsx("span", { className: 'account-menu__separator', style: style });
    };
    // ######################### Render - Account Parts #########################
    renderHeader = () => {
        const account = this.state.account;
        if (!account)
            return;
        const accountDisplay = this.props.accountDisplayModifier?.(account) ?? account.displayName ?? account.email;
        const AdditionalHeaderRenderer = ModuleFE_Utils.getRenderer(RendererKey_AccountMenu_SubHeader, { account: account });
        return _jsxs(_Fragment, { children: [_jsxs(LL_H_C, { className: 'account-menu__header', children: [_jsx(Component_AccountThumbnail, { accountId: () => account._id, acronymComposer: this.props.acronymComposer }), _jsx("div", { className: 'account-menu__header__display-name', children: accountDisplay })] }), AdditionalHeaderRenderer && _jsx(AdditionalHeaderRenderer, {}), this.renderSeparatorBar()] });
    };
    renderBody = () => {
        const account = this.state.account;
        if (!account)
            return;
        return _jsx(Grid, { className: 'account-menu__body', children: this.state.menuActions?.map((menuAction, i) => {
                return _jsx(Button, { variant: 'secondary', id: menuAction.id, disabled: menuAction.type === 'action' && menuAction.disabled, className: _className(menuAction.className), onClick: (e) => this.onActionClick(menuAction, e), children: menuAction.label }, i);
            }) });
    };
    renderFooter = () => {
        const account = this.state.account;
        if (!account)
            return;
        return _jsxs(_Fragment, { children: [this.renderSeparatorBar({ marginTop: 'auto' }), _jsxs(LL_V_C, { className: 'account-menu__footer', children: [_jsx(Button, { variant: 'primary', onClick: this.logOut, children: "Log Out" }), _jsx("div", { className: "version", children: RuntimeVersion() })] })] });
    };
    // ######################### Render - Custom Page #########################
    renderCustomPage = () => {
        const pageKey = this.state.pageKey;
        const account = this.state.account;
        if (!account)
            return;
        const className = _className('account-menu__custom-page', !!pageKey && 'show');
        return _jsxs(LL_V_L, { className: className, children: [_jsx("div", { className: 'account-menu__custom-page__back-button', onClick: this.closeCustomPage, children: "Back" }), _jsx(TS_ComponentTransition, { trigger: !!pageKey, unmountTimeout: 200, onExitDone: () => this.setState({ prevPageKey: undefined }), children: this.state.pageMap[pageKey ?? this.state.prevPageKey]?.(account, this.closeCustomPage) })] });
    };
}
//# sourceMappingURL=PopUp_AccountMenu.js.map